import type { DirectoryElements, DirectoryPublic } from '@repo/types/directory';
import type { FilePublic } from '@repo/types/files';

import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { useState } from 'react';

import type { SelectFsItemsProps } from '.';

export function useSelectFsItems(selectFsItemsProps: SelectFsItemsProps) {
  const {
    defaultSelected,
    items = [],
    multiple = true,
    fsSelectMode = 'fs',
    allow = {
      file: true,
      directory: true,
    },
    accept = '*/*',
  } = selectFsItemsProps;

  const [selected, setSelected] = useState<DirectoryElements | null>(defaultSelected ?? null);
  const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);

  /**
   * Checks if a fileType matches the accept pattern
   */
  const matchesAccept = (fileType: string) => {
    if (!accept || accept === '*/*') return true;
    const acceptList = accept.split(',').map((a) => a.trim());
    return acceptList.some((pattern) => {
      if (pattern === '*/*') return true;
      if (pattern.endsWith('/*')) {
        return fileType.startsWith(pattern.slice(0, -1));
      }
      return fileType === pattern;
    });
  };

  /**
   * Determines if an item can be selected based on type restrictions
   */
  const handleCanSelectItem = (item: DirectoryPublic | FilePublic) => {
    const isFile = getIsFSEntityFile(item);
    if (isFile && allow.file === false) return false;
    if (!isFile && allow.directory === false) return false;
    if (isFile && !matchesAccept(item.fileType)) return false;
    return true;
  };

  /**
   * Removes a specific item from the current selection
   */
  const handleRemoveSelected = (item: DirectoryPublic | FilePublic) => {
    if (!selected) return;
    const isFile: boolean = getIsFSEntityFile(item);
    if (isFile) {
      setSelected({
        ...selected,
        files: selected.files.filter((selectedItem) => selectedItem.id !== item.id),
      });
    } else {
      setSelected({
        ...selected,
        directories: selected.directories.filter((selectedItem) => selectedItem.id !== item.id),
      });
    }
  };

  /**
   * Builds a selection object from a set of item IDs
   */
  const buildSelectedFromIds = (ids: Set<string>) => {
    const files: FilePublic[] = [];
    const directories: DirectoryPublic[] = [];
    for (const it of items) {
      if (!ids.has(it.id)) continue;
      if (!handleCanSelectItem(it)) continue;
      if (getIsFSEntityFile(it)) files.push(it);
      else directories.push(it as DirectoryPublic);
    }
    return { files, directories };
  };

  /**
   * Handles single item selection without modifiers
   */
  const handleSingleSelection = (item: DirectoryPublic | FilePublic, itemIndex: number) => {
    const isFile = getIsFSEntityFile(item);
    setLastClickedIndex(itemIndex);

    setSelected(isFile ? { files: [item as FilePublic], directories: [] } : { files: [], directories: [item as DirectoryPublic] });
  };

  /**
   * Handles range selection with Shift key
   */
  const handleRangeSelection = (currentIndex: number) => {
    if (lastClickedIndex === null) return;

    const from = Math.min(lastClickedIndex, currentIndex);
    const to = Math.max(lastClickedIndex, currentIndex);

    const idsInRange = new Set(
      items
        .slice(from, to + 1)
        .filter(handleCanSelectItem)
        .map((item) => item.id)
    );

    setSelected(buildSelectedFromIds(idsInRange));
  };

  /**
   * Handles toggle selection with Ctrl/Cmd key
   */
  const handleToggleSelection = (item: DirectoryPublic | FilePublic, itemIndex: number, isAlreadySelected: boolean) => {
    setLastClickedIndex(itemIndex);

    if (isAlreadySelected) {
      handleRemoveSelected(item);
    } else {
      const isFile = getIsFSEntityFile(item);
      setSelected((prevSelected) => ({
        files: isFile ? [...(prevSelected?.files || []), item as FilePublic] : prevSelected?.files || [],
        directories: !isFile ? [...(prevSelected?.directories || []), item as DirectoryPublic] : prevSelected?.directories || [],
      }));
    }
  };

  /**
   * Handles basic mode selection (toggle behavior)
   */
  const handleBasicModeSelection = (item: DirectoryPublic | FilePublic, itemIndex: number, isAlreadySelected: boolean) => {
    if (isAlreadySelected) {
      handleRemoveSelected(item);
    } else {
      setLastClickedIndex(itemIndex);
      const isFile = getIsFSEntityFile(item);
      setSelected((prevSelected) => ({
        files: isFile ? [...(prevSelected?.files || []), item as FilePublic] : prevSelected?.files || [],
        directories: !isFile ? [...(prevSelected?.directories || []), item as DirectoryPublic] : prevSelected?.directories || [],
      }));
    }
  };

  /**
   * Main click handler that delegates to specific selection strategies
   */
  const handleClickItem = (item: DirectoryPublic | FilePublic, event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (!handleCanSelectItem(item)) return;

    const itemIndex = items.findIndex((it) => it.id === item.id);
    const isAlreadySelected = handleIsItemSelected(item);

    // Single selection mode
    if (!multiple) {
      if (fsSelectMode === 'basic' && isAlreadySelected) {
        handleRemoveSelected(item);
        return;
      }
      handleSingleSelection(item, itemIndex);
      return;
    }

    // Range selection (Shift key)
    if (event?.shiftKey) {
      handleRangeSelection(itemIndex);
      return;
    }

    // Toggle selection (Ctrl/Cmd key)
    if (event?.ctrlKey || event?.metaKey) {
      handleToggleSelection(item, itemIndex, isAlreadySelected);
      return;
    }

    // Basic mode: toggle behavior on plain click
    if (fsSelectMode === 'basic') {
      handleBasicModeSelection(item, itemIndex, isAlreadySelected);
      return;
    }

    handleSingleSelection(item, itemIndex);
  };

  /**
   * Checks if a specific item is currently selected
   */
  const handleIsItemSelected = (item: DirectoryPublic | FilePublic) => {
    const isFile: boolean = getIsFSEntityFile(item);
    return selected ? (isFile ? selected.files.some((file) => file.id === item.id) : selected.directories.some((dir) => dir.id === item.id)) : false;
  };

  return {
    state: {
      items,
      selected,
      selectedCount: (selected?.files.length || 0) + (selected?.directories.length || 0),
    },
    actions: {
      setSelected,
      handleIsItemSelected,
      handleClickItem,
      handleCanSelectItem,
      handleRemoveSelected,
    },
  };
}
